import { ArrowLeft, Clock3, History, MessageSquareText, Paperclip, Sparkles } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import type { DocumentRecord } from '../../shared/types'
import { ApiClientError, apiFetch } from '../api'
import { countDocumentUnits, formatSelection, type FormatAction } from '../editor'
import { AiAssistant, type AiProposal } from './AiAssistant'
import { AttachmentPanel } from './AttachmentPanel'
import { CommentPanel } from './CommentPanel'
import { MarkdownToolbar } from './MarkdownToolbar'
import { RevisionPanel } from './RevisionPanel'

type SaveStatus = 'saved' | 'unsaved' | 'saving' | 'error' | 'conflict'

interface Draft { title: string; content: string }

export function DocumentWorkspace({ documentId, onBack }: { documentId: string; onBack: () => void }) {
  const [document, setDocument] = useState<DocumentRecord | null>(null)
  const [draft, setDraft] = useState<Draft>({ title: '', content: '' })
  const draftRef = useRef(draft)
  const [status, setStatus] = useState<SaveStatus>('saved')
  const [loadError, setLoadError] = useState('')
  const [panel, setPanel] = useState<'history' | 'comments' | 'attachments' | null>(null)
  const [aiOpen, setAiOpen] = useState(() => localStorage.getItem('shc-docs-ai-open') === 'true')
  const [conflict, setConflict] = useState<{ local: Draft; latest: DocumentRecord } | null>(null)
  const editorRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => { draftRef.current = draft }, [draft])

  useEffect(() => {
    let active = true
    apiFetch<DocumentRecord>(`/api/documents/${documentId}`)
      .then((loaded) => {
        if (!active) return
        setDocument(loaded)
        setDraft({ title: loaded.title, content: loaded.content })
      })
      .catch((reason) => { if (active) setLoadError(reason instanceof ApiClientError ? reason.message : '文档加载失败') })
    return () => { active = false }
  }, [documentId])

  useEffect(() => {
    if (!document || conflict) return
    if (draft.title === document.title && draft.content === document.content) {
      setStatus('saved')
      return
    }
    setStatus('unsaved')
    const snapshot = draft
    const baseVersion = document.version
    const timer = window.setTimeout(async () => {
      setStatus('saving')
      try {
        const updated = await apiFetch<DocumentRecord>(`/api/documents/${document.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ ...snapshot, version: baseVersion, source: 'human' }),
        })
        setDocument(updated)
        if (draftRef.current.title === snapshot.title && draftRef.current.content === snapshot.content) {
          setDraft({ title: updated.title, content: updated.content })
          setStatus('saved')
        } else {
          setStatus('unsaved')
        }
      } catch (reason) {
        if (reason instanceof ApiClientError && reason.code === 'VERSION_CONFLICT') {
          const latest = (reason.details as { latest?: DocumentRecord } | undefined)?.latest
          if (latest) {
            setConflict({ local: draftRef.current, latest })
            setStatus('conflict')
            return
          }
        }
        setStatus('error')
      }
    }, 800)
    return () => window.clearTimeout(timer)
  }, [conflict, document, draft])

  function format(action: FormatAction) {
    const editor = editorRef.current
    if (!editor) return
    const result = formatSelection(draft.content, editor.selectionStart, editor.selectionEnd, action)
    setDraft((current) => ({ ...current, content: result.value }))
    requestAnimationFrame(() => {
      editor.focus()
      editor.setSelectionRange(result.selectionStart, result.selectionEnd)
    })
  }

  function acceptDocument(restored: DocumentRecord) {
    setDocument(restored)
    setDraft({ title: restored.title, content: restored.content })
    setStatus('saved')
  }

  function setAssistant(open: boolean) {
    setAiOpen(open)
    localStorage.setItem('shc-docs-ai-open', String(open))
    if (open) setPanel(null)
  }

  async function applyAiProposal(proposal: AiProposal) {
    if (!document) throw new Error('文档尚未加载完成')
    if (status !== 'saved') throw new Error('请等待当前修改保存后再应用 AI 建议')
    if (proposal.baseVersion !== document.version) throw new Error('文档版本已经变化，请重新生成建议')
    try {
      const updated = await apiFetch<DocumentRecord>(`/api/documents/${document.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          title: proposal.title,
          content: proposal.content,
          version: proposal.baseVersion,
          source: 'ai',
          summary: proposal.summary,
        }),
      })
      acceptDocument(updated)
    } catch (reason) {
      if (reason instanceof ApiClientError && reason.code === 'VERSION_CONFLICT') {
        const latest = (reason.details as { latest?: DocumentRecord } | undefined)?.latest
        if (latest) {
          setConflict({ local: draftRef.current, latest })
          setStatus('conflict')
        }
      }
      throw reason
    }
  }

  if (loadError) return <main className="workspace-loading"><p role="alert">{loadError}</p><button className="ghost-button" type="button" onClick={onBack}>返回文档面板</button></main>
  if (!document) return <main className="workspace-loading" aria-busy="true">正在打开文档…</main>

  const statusText = { saved: '已保存', unsaved: '尚未保存', saving: '正在保存…', error: '保存失败', conflict: '存在编辑冲突' }[status]
  return (
    <main className={`workspace-page ${panel || aiOpen ? 'with-panel' : ''}`}>
      <header className="workspace-header">
        <button className="icon-text-button" type="button" onClick={onBack}><ArrowLeft size={17} />文档面板</button>
        <div className={`save-state ${status}`} role="status" aria-live="polite"><span />{statusText}</div>
        <div className="workspace-actions">
          <button type="button" className={panel === 'history' ? 'active' : ''} onClick={() => { setAssistant(false); setPanel(panel === 'history' ? null : 'history') }} aria-label="版本记录" aria-expanded={panel === 'history'}><History size={17} />版本</button>
          <button type="button" className={panel === 'comments' ? 'active' : ''} onClick={() => { setAssistant(false); setPanel(panel === 'comments' ? null : 'comments') }} aria-label="留言" aria-expanded={panel === 'comments'}><MessageSquareText size={17} />留言</button>
          <button type="button" className={panel === 'attachments' ? 'active' : ''} onClick={() => { setAssistant(false); setPanel(panel === 'attachments' ? null : 'attachments') }} aria-label="附件" aria-expanded={panel === 'attachments'}><Paperclip size={17} />附件</button>
          <button type="button" className={aiOpen ? 'active' : ''} aria-label="AI 助手" aria-expanded={aiOpen} onClick={() => setAssistant(!aiOpen)}><Sparkles size={17} />AI</button>
        </div>
      </header>
      <section className="editor-stage">
        <article className="editor-paper">
          <input className="document-title-input" aria-label="文档标题" maxLength={120} value={draft.title} onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))} />
          <div className="editor-meta"><span><Clock3 size={14} />版本 {document.version}</span><span>{countDocumentUnits(draft.content)} 字</span><span>最后编辑：{document.lastEditor}</span></div>
          <MarkdownToolbar onFormat={format} />
          <textarea ref={editorRef} className="document-editor" aria-label="文档正文" spellCheck="true" value={draft.content} onChange={(event) => setDraft((current) => ({ ...current, content: event.target.value }))} placeholder="从这里开始写作…" />
        </article>
      </section>
      {panel === 'history' ? <RevisionPanel document={document} onRestored={acceptDocument} /> : null}
      {panel === 'comments' ? <CommentPanel documentId={document.id} /> : null}
      {panel === 'attachments' ? <AttachmentPanel documentId={document.id} /> : null}
      {aiOpen ? <AiAssistant document={document} canInteract={status === 'saved'} onApply={applyAiProposal} onClose={() => setAssistant(false)} /> : null}
      {conflict ? <div className="modal-backdrop"><section className="modal-card conflict-dialog" role="dialog" aria-modal="true" aria-labelledby="conflict-heading"><h2 id="conflict-heading">检测到编辑冲突</h2><p>服务器上的文档已由 {conflict.latest.lastEditor} 更新。我们没有覆盖任何一方的内容。</p><div className="conflict-columns"><div><strong>我的本地草稿</strong><pre>{conflict.local.content}</pre></div><div><strong>服务器版本</strong><pre>{conflict.latest.content}</pre></div></div><div className="modal-actions"><button className="ghost-button" type="button" onClick={() => void navigator.clipboard?.writeText(conflict.local.content)}>复制本地草稿</button><button className="primary-button" type="button" onClick={() => { acceptDocument(conflict.latest); setConflict(null) }}>加载服务器版本</button></div></section></div> : null}
    </main>
  )
}
